import React, { useState } from 'react';
import { ChevronLeft, PieChart } from 'lucide-react';
import { StockLogo } from '../../utils/stockLogo';
import { useCurrency, CURRENCY_FLAGS } from '../../contexts/CurrencyContext';
import AssetQuantityModal from './AssetQuantityModal';

const COLORS = {
  BOND: '#4299E1',
  CASH: '#48BB78', 
  COMMODITIES: '#F6AD55',
  ETF: '#667EEA',
  STOCK: '#F687B3',
  default: '#A0AEC0'
};

const CategoryRow = ({ 
  item,
  onPathChange,
  currentPath,
  isAsset = false,
  targetAssets = {},
  parentValue = 0,
  convertedValue = 0,
  onUpdateAsset,
  allCategories,
  onMoveAsset
}) => {
  const [showModal, setShowModal] = useState(false);
  const { convertAmount, formatAmount, selectedCurrency } = useCurrency();

  const handleClick = () => {
    if (isAsset) {
      setShowModal(true);
      return;
    }
    onPathChange(currentPath ? `${currentPath}/${item.name}` : item.name);
  };

  const handleUpdateAsset = async (symbol, newQuantity) => {
    if (onUpdateAsset) {
      await onUpdateAsset(symbol, newQuantity);
    }
  };

  const isCurrency = item.symbol?.endsWith('CURRENCY');
  const assetCurrency = isCurrency ? item.symbol.split('.')[0] : (item.currency || 'USD');

  const position = isCurrency ? parseFloat(item.quantity) || parseFloat(item.position) || 0 : 0;
  const value = convertedValue || (isCurrency ? convertAmount(position, assetCurrency, selectedCurrency) : convertAmount(item.value || 0, assetCurrency, selectedCurrency));
  const invested = value;
  const rawGain = isCurrency ? 0 : (item.gain || 0);
  const gain = convertAmount(rawGain, assetCurrency, selectedCurrency);

  const currentAllocation = parentValue > 0 ? (value / parentValue) * 100 : 0;
  const targetAllocation = item.targetAllocation || targetAssets[item.symbol]?.allocation || 0;

  const renderIcon = () => {
    if (isCurrency) {
      return (
        <div className="w-8 h-8 rounded flex items-center justify-center">
          <img 
            src={`/images/currencys/${assetCurrency.toLowerCase()}.png`}
            alt={assetCurrency}
            className="h-6 w-6"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = `<span class='text-xs font-medium'>${assetCurrency}</span>`;
            }}
          />
        </div>
      );
    }

    if (!isAsset) {
      const color = COLORS[item.name] || COLORS.default;
      return (
        <div 
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${color}20` }}
        >
          <PieChart className="w-5 h-5" style={{ color }} />
        </div>
      );
    }

    return <StockLogo symbol={item.symbol} symbolInfo={item} />;
  };

  const renderName = () => {
    if (isCurrency) {
      const formattedOriginalAmount = formatAmount(position, assetCurrency);
      const currencyName = CURRENCY_FLAGS[assetCurrency]?.name || assetCurrency;
      return (
        <>
          <div className="font-medium text-gray-900 dark:text-gray-100">
            {currencyName}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {`${assetCurrency} • ${formattedOriginalAmount}`}
          </div>
        </>
      );
    }

    if (isAsset) {
      return (
        <>
          <div className="font-medium text-gray-900 dark:text-gray-100">
            {item.description || item.name}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {item.symbol} · {item.quantity || 0} adet · {assetCurrency}
          </div>
        </>
      );
    }

    return (
      <>
        <div className="font-medium text-gray-900 dark:text-gray-100">{item.name}</div>
        {item.items?.length > 0 && (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {item.items.length} öğe
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <tr 
        className={`border-b border-gray-200 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-800/50 
          ${isAsset ? 'cursor-pointer' : ''}`}
        onClick={handleClick}
      >
        <td className="py-3">
          <div className="flex items-center gap-3">
            {renderIcon()}
            <div className="flex-1 min-w-0">
              {renderName()}
            </div>
          </div>
        </td>
        <td className="text-right">
          <div className="font-medium text-gray-900 dark:text-gray-100">
            {formatAmount(value)}
          </div>
          {!isCurrency && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {formatAmount(invested)}
            </div>
          )}
        </td>
        <td className="hidden md:table-cell text-right">
          <div className={(gain || 0) >= 0 ? "text-green-500" : "text-red-500"}>
            {formatAmount(Math.abs(gain))}
          </div>
          <div className={`text-sm ${(item.gainPercent || 0) >= 0 ? "text-green-500" : "text-red-500"}`}>
            {(item.gainPercent || 0) >= 0 ? "↑" : "↓"} {Math.abs(item.gainPercent || 0).toFixed(2)}%
          </div>
        </td>
        <td className="text-right">
          <div className="font-medium text-gray-900 dark:text-gray-100">
            {currentAllocation.toFixed(2)}%
          </div>
          {targetAllocation > 0 && (
            <div className={`text-sm ${
              Math.abs(currentAllocation - targetAllocation) >= 5 
                ? "text-red-500" 
                : "text-gray-500 dark:text-gray-400"
            }`}>
              Hedef: {targetAllocation.toFixed(2)}%
            </div>
          )}
        </td>
      </tr>

      {showModal && (
        <AssetQuantityModal
          asset={item}
          onClose={() => setShowModal(false)}
          onUpdate={handleUpdateAsset}
          allCategories={allCategories}
          onMove={onMoveAsset}
        />
      )}
    </>
  );
};

const PortfolioTable = ({ 
  data, 
  currentPath, 
  onPathChange, 
  targetPortfolio,
  onUpdateAsset,
  allCategories,
  onMoveAsset 
}) => {
  const { convertAmount, selectedCurrency } = useCurrency();

  const getTargetAssets = () => {
    if (!currentPath || !targetPortfolio) return {};

    const parts = currentPath.split('/');
    let current = targetPortfolio[parts[0]];
    if (!current) return {};

    if (parts.length > 1 && current.subCategories) {
      current = current.subCategories[parts[1]];
    }

    return current?.assets || {};
  };

  const calculateTotalValue = (items) => {
    let total = 0;
    for (const item of items) {
      if (item.items && item.items.length > 0) {
        total += calculateTotalValue(item.items);
      } else {
        const currency = item.symbol?.endsWith('CURRENCY') 
          ? item.symbol.split('.')[0] 
          : (item.currency || 'USD');
        
        if (item.symbol?.endsWith('CURRENCY')) {
          const position = parseFloat(item.position) || parseFloat(item.quantity) || 0;
          total += convertAmount(position, currency, selectedCurrency);
        } else {
          total += convertAmount(item.value || 0, currency, selectedCurrency);
        }
      }
    }
    return total;
  };

  const processData = (items) => {
    const totalPortfolioValue = calculateTotalValue(data);
    
    return items.map(item => {
      if (item.items && item.items.length > 0) {
        const categoryValue = calculateTotalValue([item]);
        return {
          ...item,
          items: processData(item.items),
          convertedValue: categoryValue,
          allocation: totalPortfolioValue > 0 ? (categoryValue / totalPortfolioValue) * 100 : 0
        };
      }

      const currency = item.symbol?.endsWith('CURRENCY') 
        ? item.symbol.split('.')[0] 
        : (item.currency || 'USD');
      
      let itemValue;
      if (item.symbol?.endsWith('CURRENCY')) {
        const position = parseFloat(item.position) || parseFloat(item.quantity) || 0;
        itemValue = convertAmount(position, currency, selectedCurrency);
      } else {
        itemValue = convertAmount(item.value || 0, currency, selectedCurrency);
      }

      return {
        ...item,
        convertedValue: itemValue,
        allocation: totalPortfolioValue > 0 ? (itemValue / totalPortfolioValue) * 100 : 0
      };
    });
  };

  const targetAssets = getTargetAssets();
  const totalValue = calculateTotalValue(data);
  const processedData = processData(data);

  return (
    <div className="h-[500px] flex flex-col bg-white dark:bg-[#151934] rounded-lg shadow-sm">
      <div className="flex-none p-4">
        {currentPath && (
          <button
            onClick={() => {
              const parts = currentPath.split('/');
              parts.pop();
              onPathChange(parts.join('/'));
            }}
            className="flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
          >
            <ChevronLeft className="w-4 h-4" />
            Geri
          </button>
        )}
      </div>

      <div className="flex-1 overflow-auto px-4 pb-4">
        <table className="w-full">
          <thead className="sticky top-0 bg-white dark:bg-[#151934] z-10">
            <tr className="border-b border-gray-200 dark:border-gray-700/50">
              <th className="text-left pb-2 font-medium text-gray-600 dark:text-gray-400">İsim</th>
              <th className="text-right pb-2 font-medium text-gray-600 dark:text-gray-400">Değer/Yatırım</th>
              <th className="hidden md:table-cell text-right pb-2 font-medium text-gray-600 dark:text-gray-400">Kazanç</th>
              <th className="text-right pb-2 font-medium text-gray-600 dark:text-gray-400">Dağılım</th>
            </tr>
          </thead>
          <tbody>
            {processedData.map((item) => (
              <CategoryRow
                key={item.name || item.symbol}
                item={item}
                onPathChange={onPathChange}
                currentPath={currentPath}
                isAsset={!!item.symbol}
                targetAssets={targetAssets}
                parentValue={totalValue}
                convertedValue={item.convertedValue}
                onUpdateAsset={onUpdateAsset}
                allCategories={allCategories}
                onMoveAsset={onMoveAsset}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PortfolioTable;